import React from 'react';
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Radio,
  Space,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import type { ExpV2Layer, ExpV2Status, ExpV2UserList } from '@/types/experimentsV2';
import { PreRegistrationForm } from './PreRegistrationForm';
import { TargetingEditor } from './TargetingEditor';
import { VariantsEditor } from './VariantsEditor';
import { HOLDOUT_LAYER, draftProblems, preRegistrationMissing, type DraftForm } from './draftForm';

export interface EditorBodyProps {
  mode: 'create' | 'edit';
  /** Status of the saved test; only drafts can be edited. */
  status: ExpV2Status;
  form: DraftForm;
  onChange: (form: DraftForm) => void;
  layers: ExpV2Layer[];
  lists: ExpV2UserList[];
  /** The server's refusal of the last save, word for word. */
  serverError: string | null;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}

const section = (title: string) => (
  <Divider orientation="left" style={{ marginTop: 24 }}>
    {title}
  </Divider>
);

export const EditorBody: React.FC<EditorBodyProps> = (p) => {
  const { form, onChange } = p;
  if (p.mode === 'edit' && p.status !== 'draft') {
    return (
      <Alert
        type="info"
        showIcon
        message="Only drafts can be edited"
        description="Params, weights and targeting never change while a test serves players. Make a new test instead."
      />
    );
  }

  const set = (patch: Partial<DraftForm>) => onChange({ ...form, ...patch });
  const problems = draftProblems(form);
  const isTest = form.kind === 'experiment';
  const layers = p.layers.filter((l) => l.key !== HOLDOUT_LAYER);

  return (
    <Form layout="vertical" style={{ maxWidth: 820 }}>
      {p.serverError && (
        <Alert
          type="error"
          showIcon
          message="The server refused the save"
          description={p.serverError}
          style={{ marginBottom: 16 }}
        />
      )}

      {section(isTest ? 'Test' : 'Flag')}
      <Form.Item
        label="Key"
        required
        extra="Fixed once created. Also the exposure key and the GA4 property exp_<key>."
      >
        <Input
          id="expv2-key"
          value={form.key}
          disabled={p.mode === 'edit'}
          onChange={(e) => set({ key: e.target.value.trim() })}
        />
      </Form.Item>
      <Form.Item label="Name" required>
        <Input id="expv2-name" value={form.name} onChange={(e) => set({ name: e.target.value })} />
      </Form.Item>
      <Form.Item label="Layer" required extra="A player is in at most one test per layer.">
        <Radio.Group
          value={form.layerKey}
          optionType="button"
          onChange={(e) => set({ layerKey: e.target.value as string })}
        >
          {layers.map((l) => (
            <Radio.Button key={l.key} value={l.key}>
              <span data-layer-option={l.key}>{l.key}</span>
            </Radio.Button>
          ))}
        </Radio.Group>
        {layers.length === 0 && (
          <Typography.Text type="secondary">Create a layer first (Layers tab).</Typography.Text>
        )}
      </Form.Item>
      <Form.Item
        label="Reserved %"
        required
        extra={
          isTest
            ? 'Share of the layer this test holds. Start opens a small part; Go to target opens all of it.'
            : 'Share of matching players the flag can reach. Start opens a small part; Raise % adds more.'
        }
      >
        <InputNumber
          id="expv2-range"
          min={0.1}
          max={100}
          step={0.1}
          addonAfter="%"
          value={form.rangePercent}
          onChange={(n) => set({ rangePercent: n ?? 0 })}
        />
      </Form.Item>
      <Form.Item>
        <Checkbox
          id="expv2-economy"
          checked={form.economy}
          onChange={(e) => set({ economy: e.target.checked })}
        >
          Economy test (rewards, prices, drops)
        </Checkbox>
        <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
          Start, Go to target, Raise %, Resume and Roll out winner then wait for a second admin.
        </Typography.Paragraph>
      </Form.Item>

      {section('Variants and params')}
      <VariantsEditor form={form} onChange={(variants) => set({ variants })} />

      {section('Targeting')}
      <TargetingEditor
        value={form.targeting}
        lists={p.lists}
        onChange={(targeting) => set({ targeting })}
      />

      {section('Version gate')}
      <Form.Item
        label="Required app version"
        extra="Older builds are never assigned; an unknown version counts as too old."
      >
        <Input
          id="expv2-required-version"
          placeholder="e.g. 0.4.5"
          value={form.requiredAppVersion}
          onChange={(e) => set({ requiredAppVersion: e.target.value })}
        />
      </Form.Item>
      <Space wrap>
        <Form.Item label="Android build at least">
          <Input
            id="expv2-build-android"
            value={form.buildAndroid}
            onChange={(e) => set({ buildAndroid: e.target.value })}
          />
        </Form.Item>
        <Form.Item label="iOS build at least">
          <Input
            id="expv2-build-ios"
            value={form.buildIos}
            onChange={(e) => set({ buildIos: e.target.value })}
          />
        </Form.Item>
      </Space>

      {section('Schedule')}
      <Typography.Paragraph type="secondary">
        Optional. Times are in your browser&apos;s time zone. Without a start time the test starts
        when an admin presses Start.
      </Typography.Paragraph>
      <Space wrap>
        <Form.Item label="Starts at">
          <DatePicker
            id="expv2-starts"
            showTime
            value={form.startsAt ? dayjs(form.startsAt) : null}
            onChange={(d) => set({ startsAt: d ? d.toISOString() : null })}
          />
        </Form.Item>
        <Form.Item label="Ends at">
          <DatePicker
            id="expv2-ends"
            showTime
            value={form.endsAt ? dayjs(form.endsAt) : null}
            onChange={(d) => set({ endsAt: d ? d.toISOString() : null })}
          />
        </Form.Item>
      </Space>

      {isTest && (
        <>
          {section('Pre-registration')}
          <PreRegistrationForm
            value={form.pre}
            missing={preRegistrationMissing(form)}
            onChange={(pre) => set({ pre })}
          />
        </>
      )}

      <Divider />
      {problems.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message="Fix these before saving"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {problems.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          }
          style={{ marginBottom: 12 }}
        />
      )}
      <Space>
        <Button type="primary" loading={p.saving} disabled={problems.length > 0} onClick={p.onSave}>
          {p.mode === 'create' ? 'Save draft' : 'Save changes'}
        </Button>
        <Button onClick={p.onCancel}>Cancel</Button>
      </Space>
    </Form>
  );
};
